import { useI18n } from "vue-i18n";
import html_iway from "./template.iway.html?raw";
import ErrorMessage from "../../Shared/ErrorMessage";
import { computed, defineComponent, inject, ref, watch } from "vue";
import { priceFormatterKey } from "../../../infrastructure/price_formatter";
import { type ProductVariantDto } from "../../../infrastructure/gfo_api/order_api";
import { orderServiceKey } from "../../../infrastructure/gfo_api/order_service";
import MoreLessContent from "../../Shared/MoreLessContent";

const app_theme = import.meta.env.VITE_APP_THEME;
let html = "";
if ("iway" == app_theme) {
  html = html_iway;
} else if ("lic" == app_theme) {
  html = html_iway;
} else if ("sak" == app_theme) {
  html = html_iway;
}

interface Props {
  variant: ProductVariantDto | null;
  isRecommendation: boolean|null;
  className: string|null;
}
export default defineComponent({
  template: html,
  components: { ErrorMessage, MoreLessContent },
  props: {
    variant: {
      type: Object as () => ProductVariantDto,
      default: null,
    },
    isRecommendation: {
      type: Boolean,
      default: null
    },
    className: {
      type: String,
      default: ''
    }
  },
  emits: {
    addQuantity: (variant: ProductVariantDto, quantity: number) => {
      return variant !== undefined && typeof quantity === "number";
    },
  },
  setup(props: Props, { emit }) {
    const { t } = useI18n();
    const orderService = inject(orderServiceKey)
    const priceFormatter = inject(priceFormatterKey);

    // computed, variables, refs
    const content = computed(() => {
      let c = props.variant?.description?.trim() ?? "";
      if (hasMoreFeatures.value) {
        c += `<dl class="grid grid-cols-2 gap-x-4 gap-y-1 ">`;
        for (const [k, v] of Object.entries(moreFeatures.value)) {
          c += `<dt>${k}</dt><dd>${v}</dd>`;
        }
        c += `</dl>`;
      }
      return c;
    })

    const currentQuantity = computed(() => {
      const res= orderService?.currentItems?.value.reduce((sum, it) => {
        if (it.product_variant_id == props.variant?.id){
          sum = sum+it.qty;
        }
        return sum;
      }, 0);
      return res ?? 0;
    })

    const isChecked = ref(false);
    const vatType = computed(() => {
      return orderService?.currentVatType.value;
    });

    const productAllowMultiple = computed(() => {
      let res = props.variant?.product_allow_multiple;
      if (typeof res === "undefined"){
        return false;
      }
      return res;
    });
    const moreFeatures = computed(() => {
      let features: Record<string, string> = {}
      if ('' != `${props.variant?.features}`.trim()){
        const parts = `${props.variant?.features}`.split("\n");
        parts.forEach(function(part){
          const subparts = part.split(":", 2);
          features[subparts[0].trim()] = subparts[1]?.trim() || "";
        })
      }
      return features;
    })
    const hasMoreFeatures = computed(() => Object.keys(moreFeatures.value).length > 0);

    const geschenkt = computed(() => {
      return props.variant?.tags.includes("geschenkt") ?? false;
    });
    const kostenlosLeihweise = computed(() => {
      return props.variant?.tags.includes("kostenlos_leihweise") ?? false;
    });
    const nichtsAnzeigen = computed(() => {
      return props.variant?.tags.includes("nichts_anzeigen") ?? false;
    });
    const kostenlos = computed(() => {
      if (props.variant?.price_rec_incl == 0 
        && props.variant?.price_rec == 0
        && props.variant?.price_fix_incl == 0
        && props.variant?.price_fix == 0){
          return true;
        }
        return false;

    })

    // Methods
    // Wenn Benutzer Checkbox ändert, update den Server
    const toggle = async () => {
      if (!props.variant) {
        return;
      }
      try {
        if (isChecked.value) {
          await orderService?.addToCart(props.variant?.id || 0, "", 1);
        } else {
          await orderService?.removeFromCart(props.variant?.id ?? 0, "", currentQuantity.value);
        }
      } catch (error) {
        // Rollback zu vorherigem State wenn Server Fehler
        console.error('Cart update failed:', error);
      } finally {
        isChecked.value = currentQuantity.value > 0;
      }
    };
    const add = () => {
      if (!props.variant) {
        return;
      }
      emit("addQuantity", props.variant, 1);
    };
    const reduce = () => {
      if (!props.variant) {
        return;
      }
      emit("addQuantity", props.variant, -1);
    };


    // Watcher
    // Synchronisiere Server-State mit lokalem checkbox state
    watch(currentQuantity, (newQty) => {
      isChecked.value = newQty > 0;
    }, { immediate: true });

    return {
      add,
      reduce,
      t,
      content,
      currentQuantity,
      priceFormatter,
      productAllowMultiple,
      hasMoreFeatures,
      isChecked,
      toggle,
      vatType,
      geschenkt,
      kostenlosLeihweise,
      nichtsAnzeigen,
      kostenlos,
      moreFeatures,
    };
  },
});
